import { useState, useEffect } from "react";
import { Heart, Users, Calendar, TrendingUp, DollarSign, Mail } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ImpactTracker = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animatedStats, setAnimatedStats] = useState({
    total_raised: 0,
    volunteer_count: 0,
    lives_impacted: 0,
    community_members: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/impact`);
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching impact stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();

    // Refresh stats every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  // Animate numbers counting up
  useEffect(() => {
    if (!stats) return;

    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;

    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3);

      setAnimatedStats({
        total_raised: Math.round(stats.total_raised * easeOut * 100) / 100,
        volunteer_count: Math.round(stats.volunteer_count * easeOut),
        lives_impacted: Math.round(stats.lives_impacted * easeOut),
        community_members: Math.round(stats.community_members * easeOut)
      });

      if (currentStep >= steps) {
        clearInterval(interval);
        setAnimatedStats({
          total_raised: stats.total_raised,
          volunteer_count: stats.volunteer_count,
          lives_impacted: stats.lives_impacted,
          community_members: stats.community_members
        });
      }
    }, stepDuration);

    return () => clearInterval(interval);
  }, [stats]);

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-[#0056D2] to-[#0044A5] rounded-3xl p-8">
        <div className="flex justify-center">
          <div className="spinner w-8 h-8 border-2 border-white/30 border-t-white" />
        </div>
      </div>
    );
  }

  const impactItems = [
    {
      icon: DollarSign,
      value: `$${animatedStats.total_raised.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      label: "Total Raised",
      subtext: `${stats?.donation_count || 0} donations`,
      color: "bg-green-500"
    },
    {
      icon: Users,
      value: animatedStats.volunteer_count.toLocaleString(),
      label: "Active Volunteers",
      subtext: "Ready to serve",
      color: "bg-blue-500"
    },
    {
      icon: Heart,
      value: animatedStats.lives_impacted.toLocaleString(),
      label: "Lives Impacted",
      subtext: "Through our programs",
      color: "bg-rose-500"
    },
    {
      icon: Mail,
      value: animatedStats.community_members.toLocaleString(),
      label: "Community Members",
      subtext: "Growing together",
      color: "bg-amber-500"
    }
  ];

  return (
    <div 
      className="bg-gradient-to-br from-[#0056D2] to-[#0044A5] rounded-3xl p-8 md:p-12 relative overflow-hidden"
      data-testid="impact-tracker"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
      
      <div className="relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-4">
            <TrendingUp className="w-4 h-4 text-green-300" />
            <span className="text-white/90 text-sm font-medium">Live Impact Tracker</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-3">
            Together, We're Making a Difference
          </h2>
          <p className="text-blue-100 max-w-2xl mx-auto">
            Real-time updates on how your contributions are transforming communities and building peace.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {impactItems.map((item, index) => (
            <div 
              key={index}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 md:p-6 text-center group hover:bg-white/15 transition-colors duration-300"
              data-testid={`impact-stat-${index}`}
            >
              <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <item.icon className="w-6 h-6 text-white" />
              </div>
              <p className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white mb-1">
                {item.value}
              </p>
              <p className="text-white font-medium text-sm md:text-base">{item.label}</p>
              <p className="text-blue-200 text-xs mt-1">{item.subtext}</p>
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div className="mt-10 max-w-2xl mx-auto">
          <div className="flex justify-between text-sm text-blue-100 mb-2">
            <span>2026 Goal: $50,000</span>
            <span>{Math.min(100, Math.round((stats?.total_raised || 0) / 500)).toFixed(0)}%</span>
          </div>
          <div className="h-3 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-1000"
              style={{ width: `${Math.min(100, ((stats?.total_raised || 0) / 500) * 100)}%` }}
            />
          </div>
          <p className="text-center text-blue-100 text-sm mt-3">
            Every dollar brings us closer to a more peaceful world
          </p>
        </div>
      </div>
    </div>
  );
};

export default ImpactTracker;
